import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFormStats } from '../hooks';
import { useFormResponses, useDeleteFormResponse } from '../hooks';
import { formsApi } from '../services/formsApi';
import type { FormFieldType, FormSubmissionSource } from '@troia-v3/types';
import {
  BarChart, Bar, XAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { toast } from 'sonner';
import { Breadcrumb, ListPagePagination, PeriodPicker } from '@/shared/components/ui';
import type { PeriodPickerSegment, PeriodPickerValue } from '@/shared/components/ui';
import { customRangeToIso } from '@/shared/utils/customPeriodRange';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { Icon } from '@/shared/icons';
import { FormRespondentCell, RESPONDENT_CSV_HEADERS, respondentCsvValues } from '../components/FormRespondentCell';

/**
 * De onde veio cada resposta (2026-09-28). O relatório junta o link público
 * com as respostas gravadas em lead — pela automação, pela captura por QR ou
 * pela aba Formulários do lead. Resposta antiga sem origem = link público.
 */
const ORIGEM: Record<FormSubmissionSource, string> = {
  public_link: 'Link público',
  automation: 'Automação',
  lead_capture: 'Captura por QR',
  lead: 'Aba do lead',
};

const CHART_COLORS = ['#8b5cf6', '#06b6d4', '#f59e0b', '#10b981', '#ef4444', '#ec4899', '#6366f1', '#14b8a6'];

type ReportPeriod = 'week' | 'month' | 'quarter';
const PERIOD_DAYS: Record<ReportPeriod, number> = { week: 7, month: 30, quarter: 90 };
const PERIOD_SEGMENTS: ReadonlyArray<PeriodPickerSegment<ReportPeriod>> = [
  { key: 'week', label: 'Semana' },
  { key: 'month', label: 'Mês' },
  { key: 'quarter', label: 'Trimestre' },
];

/**
 * Período do relatório (2026-09-30; Personalizado em 2026-10-16). Semana/Mês/
 * Trimestre = hoje e os N−1 dias corridos antes, a partir da meia-noite local.
 * Personalizado = do primeiro dia (meia-noite) ao fim do último dia, no horário
 * local. O fuso vai junto para o backend contar os dias igual à tela. Filtra
 * números, gráfico, conclusão por campo e tabela.
 */
export const periodQuery = (
  period: PeriodPickerValue<ReportPeriod>,
  agora: Date = new Date(),
): { startDate: string; endDate?: string; timezone: string } => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (period.mode === 'custom') {
    if (period.start && period.end) return { ...customRangeToIso(period.start, period.end), timezone };
    // Personalizado sem as duas datas não chega aqui pelo seletor; cai no Mês.
    return periodQuery({ mode: 'month' }, agora);
  }
  const inicio = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() - (PERIOD_DAYS[period.mode] - 1));
  return { startDate: inicio.toISOString(), timezone };
};

/** `2026-09-29` → `29/09`. */
const diaCurto = (date: string): string => `${date.slice(8, 10)}/${date.slice(5, 7)}`;

export const FormReportPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();
  // Período (#264; de verdade desde 2026-09-30): números, gráfico, conclusão
  // por campo e tabela valem só para ele.
  const [period, setPeriodState] = useState<PeriodPickerValue<ReportPeriod>>({ mode: 'month' });
  const [responsesPage, setResponsesPage] = useState(1);
  const setPeriod = (p: PeriodPickerValue<ReportPeriod>): void => {
    setPeriodState(p);
    setResponsesPage(1);
  };
  const periodo = useMemo(() => periodQuery(period), [period]);

  const { data: form } = useForm(id);
  const { data: stats, isLoading: statsLoading } = useFormStats(id, periodo);
  const { data: responses, isLoading: responsesLoading } = useFormResponses(id, {
    formId: id || '',
    page: responsesPage,
    limit: 20,
    startDate: periodo.startDate,
    ...(periodo.endDate !== undefined ? { endDate: periodo.endDate } : {}),
  });
  const deleteResponse = useDeleteFormResponse();

  const handleExport = async () => {
    if (!id) return;
    try {
      const data = await formsApi.exportResponses(id);
      // Build CSV
      if (!form?.fields || data.length === 0) {
        toast.error('Nenhuma resposta para exportar');
        return;
      }
      const headers = [
        ...RESPONDENT_CSV_HEADERS,
        ...form.fields
          .filter(f => f.type !== ('heading' as FormFieldType) && f.type !== ('paragraph' as FormFieldType))
          .map(f => f.label),
      ];
      const rows = data.map(sub => [
        ...respondentCsvValues(sub),
        ...form.fields
          .filter(f => f.type !== ('heading' as FormFieldType) && f.type !== ('paragraph' as FormFieldType))
          .map(f => {
            const answer = sub.answers.find(a => a.fieldId === f.id);
            const val = answer?.value;
            if (Array.isArray(val)) return val.join('; ');
            return String(val ?? '');
          }),
      ]);
      const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c.replace(/"/g, '""')}"`).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${form.name}-respostas.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Exportação concluída!');
    } catch {
      toast.error('Erro ao exportar');
    }
  };

  // #264: gráfico "Respostas por dia" filtrado pelo período (últimos N dias com dados).
  const chartData = useMemo(() => stats?.responsesByDate ?? [], [stats]);
  // #265: conclusão por campo = totalAnswered / totalRespostas.
  const fieldCompletion = useMemo(() => {
    if (!stats || stats.totalResponses === 0) return [];
    return stats.fieldStats.map((fs) => ({
      fieldId: fs.fieldId,
      label: fs.fieldLabel,
      answered: fs.totalAnswered,
      pct: Math.round((fs.totalAnswered / stats.totalResponses) * 100),
    }));
  }, [stats]);
  // #269: colunas da tabela de respostas = campos do form (sem layout).
  const reportColumns = useMemo(
    () =>
      (form?.fields || [])
        .filter((f) => f.type !== ('heading' as FormFieldType) && f.type !== ('paragraph' as FormFieldType))
        .map((f) => ({ id: f.id, label: f.label })),
    [form],
  );

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* Header — toolbar 3 zonas (DS): breadcrumb à esquerda + ação à direita */}
      <div
        className="flex-shrink-0 border-b"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderColor: 'var(--theme-card-border)',
        }}
      >
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8">
          <div className="flex items-center justify-between gap-3 py-3.5">
            {/* Zona esquerda: Breadcrumb shared (F25-31 — nunca seta voltar) */}
            <Breadcrumb
              items={[
                {
                  label: 'Automação',
                  onNavigate: () => navigate('/automation/forms'),
                  testid: 'form-report-breadcrumb-automation',
                },
                {
                  label: 'Formulários',
                  onNavigate: () => navigate('/automation/forms'),
                  testid: 'form-report-breadcrumb-forms',
                },
                { label: form?.name || 'Relatório', testid: 'form-report-breadcrumb-current' },
              ]}
              mobileLegend="Automação · Formulários"
              data-testid="form-report-breadcrumb"
            />

            {/* Zona direita: período (desktop, mesma linha) + exportar */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="hidden sm:block">
                <PeriodPicker value={period} onChange={setPeriod} segments={PERIOD_SEGMENTS} data-testid="form-report-period" />
              </div>
              <button
                onClick={handleExport}
                data-testid="form-report-export"
                className="flex items-center gap-2 px-3 h-9 rounded-lg text-sm font-medium border transition-colors flex-shrink-0"
                style={{
                  borderColor: 'var(--theme-card-border)',
                  color: 'var(--theme-card-text)',
                  backgroundColor: 'var(--theme-card-background)',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--theme-card-background)'}
              >
                <Icon name="download" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
                <span className="hidden sm:inline">Exportar</span>
              </button>
            </div>
          </div>

          {/* Seletor de período — mobile (linha própria; no desktop fica na toolbar) */}
          <div className="sm:hidden pb-2.5">
            <PeriodPicker value={period} onChange={setPeriod} segments={PERIOD_SEGMENTS} data-testid="form-report-period-m" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8">
          <div className="space-y-6">
              {/* Scorecards */}
              {stats && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <MetricCard label="Respostas no período" value={stats.totalResponses} testId="form-report-total" />
                  <MetricCard label="Respostas hoje" value={stats.todayResponses} />
                  <MetricCard label="Tempo médio" value={`${Math.round(stats.averageCompletionTime)}s`} />
                  {/* Visualização não tem data: a taxa é desde a publicação. */}
                  <MetricCard label="Taxa de conclusão (desde o início)" value={`${stats.completionRate}%`} />
                </div>
              )}

              {/* Conclusão + Respostas por dia — grid 2-col no desktop (mockup rep-grid) */}
              <div className="grid lg:grid-cols-2 gap-6">
              {/* Conclusão por campo — funil horizontal (#265) */}
              {fieldCompletion.length > 0 && (
                <ReportCard title="Conclusão por campo">
                  <div className="space-y-3">
                    {fieldCompletion.map((fc) => (
                      <div key={fc.fieldId} className="flex items-center gap-3" data-testid={`form-report-completion-${fc.fieldId}`}>
                        <span className="text-sm w-40 flex-shrink-0 truncate" style={{ color: 'var(--theme-card-text)' }}>{fc.label}</span>
                        <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--theme-border)' }}>
                          <div className="h-full rounded-full" style={{ width: `${fc.pct}%`, backgroundColor: 'var(--theme-primary)' }} />
                        </div>
                        <span className="text-xs tabular-nums w-14 text-right flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }}>{fc.pct}%</span>
                      </div>
                    ))}
                  </div>
                </ReportCard>
              )}

              {/* Responses Over Time Chart — filtrado pelo período (#264) */}
              {stats && chartData.length > 0 && (
                <ReportCard title="Respostas por dia">
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={chartData}>
                      <XAxis dataKey="date" tickFormatter={diaCurto} tick={{ fontSize: 11, fill: 'var(--theme-muted-text)' }} axisLine={false} tickLine={false} />
                      <Tooltip labelFormatter={(label) => diaCurto(String(label))} formatter={(value) => [value, 'Respostas']} />
                      <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={34}>
                        {chartData.map((d, idx) => {
                          const weekday = new Date(d.date).getUTCDay();
                          const isWeekend = weekday === 0 || weekday === 6;
                          return <Cell key={idx} fill={isWeekend ? '#c4b5fd' : '#8b5cf6'} />;
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ReportCard>
              )}
              </div>

              {/* Per-Field Stats */}
              {stats && stats.fieldStats.map((fieldStat) => (
                <ReportCard
                  key={fieldStat.fieldId}
                  title={fieldStat.fieldLabel}
                  aside={`${fieldStat.totalAnswered} respostas`}
                >
                  {/* Pie chart for choice fields */}
                  {fieldStat.optionCounts && fieldStat.optionCounts.length > 0 && (
                    <div className="flex flex-col sm:flex-row items-start gap-6">
                      <div className="w-48 h-48 flex-shrink-0">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={fieldStat.optionCounts as unknown as Record<string, unknown>[]}
                              dataKey="count"
                              nameKey="option"
                              cx="50%"
                              cy="50%"
                              outerRadius={70}
                            >
                              {fieldStat.optionCounts.map((_, idx) => (
                                <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex-1 w-full space-y-2">
                        {fieldStat.optionCounts.map((opt, idx) => (
                          <div key={opt.option} className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }} />
                            <span className="text-sm flex-1 truncate" style={{ color: 'var(--theme-card-text)' }}>{opt.option}</span>
                            <span className="text-sm font-medium tabular-nums" style={{ color: 'var(--theme-card-text)' }}>{opt.count}</span>
                            <span className="text-xs tabular-nums" style={{ color: 'var(--theme-muted-text)' }}>({opt.percentage}%)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Numeric stats */}
                  {fieldStat.average !== undefined && (
                    <div className="flex gap-8">
                      <div>
                        <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>Média</p>
                        <p className="text-lg font-semibold tabular-nums" style={{ color: 'var(--theme-card-text)' }}>{fieldStat.average}</p>
                      </div>
                      <div>
                        <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>Mínimo</p>
                        <p className="text-lg font-semibold tabular-nums" style={{ color: 'var(--theme-card-text)' }}>{fieldStat.min}</p>
                      </div>
                      <div>
                        <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>Máximo</p>
                        <p className="text-lg font-semibold tabular-nums" style={{ color: 'var(--theme-card-text)' }}>{fieldStat.max}</p>
                      </div>
                    </div>
                  )}

                  {/* Text fields - just show answered count */}
                  {!fieldStat.optionCounts && fieldStat.average === undefined && (
                    <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                      {fieldStat.totalAnswered} respostas, {fieldStat.skipped} pularam
                    </p>
                  )}
                </ReportCard>
              ))}

              {statsLoading && (
                <div className="text-center py-12">
                  <p style={{ color: 'var(--theme-muted-text)' }}>Carregando estatísticas...</p>
                </div>
              )}
            </div>

          {/* Respostas — tabela em página única (#269) */}
          <div className="mt-6" data-testid="form-report-responses">
              <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--theme-card-text)' }}>
                Respostas{responses ? ` (${responses.total})` : ''}
              </h3>
              {responsesLoading && (
                <div className="text-center py-12">
                  <p style={{ color: 'var(--theme-muted-text)' }}>Carregando respostas...</p>
                </div>
              )}

              {!responsesLoading && responses && responses.items.length === 0 && (
                <div className="text-center py-12">
                  <Icon name="list" className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--theme-muted-text)' }} />
                  <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                    Nenhuma resposta recebida ainda
                  </p>
                </div>
              )}

              {!responsesLoading && responses && responses.items.length > 0 && (
                <div className="space-y-4">
                  <div
                    className="border rounded-xl overflow-x-auto"
                    style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)' }}
                  >
                    <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th className="text-left font-semibold px-3 py-2.5 whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>#</th>
                          <th className="text-left font-semibold px-3 py-2.5 whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>Respondido por</th>
                          {reportColumns.map((col) => (
                            <th key={col.id} className="text-left font-semibold px-3 py-2.5 whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>
                              {col.label}
                            </th>
                          ))}
                          <th className="text-left font-semibold px-3 py-2.5 whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>Data</th>
                          <th className="text-left font-semibold px-3 py-2.5 whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>Origem</th>
                          <th className="px-3 py-2.5" />
                        </tr>
                      </thead>
                      <tbody>
                        {responses.items.map((submission, idx) => (
                          <tr key={submission.id} style={{ borderTop: '1px solid var(--theme-card-border)' }} data-testid={`form-report-row-${submission.id}`}>
                            <td className="px-3 py-2.5 tabular-nums" style={{ color: 'var(--theme-muted-text)' }}>
                              #{((responsesPage - 1) * 20) + idx + 1}
                            </td>
                            <td className="px-3 py-2.5">
                              <FormRespondentCell submission={submission} />
                            </td>
                            {reportColumns.map((col) => {
                              const ans = submission.answers.find((a) => a.fieldId === col.id);
                              const val = ans?.value;
                              return (
                                <td key={col.id} className="px-3 py-2.5 max-w-[220px] truncate" style={{ color: 'var(--theme-card-text)' }}>
                                  {Array.isArray(val) ? val.join(', ') : String(val ?? '—')}
                                </td>
                              );
                            })}
                            <td className="px-3 py-2.5 whitespace-nowrap tabular-nums" style={{ color: 'var(--theme-muted-text)' }}>
                              {new Date(submission.submittedAt as unknown as string).toLocaleString('pt-BR')}
                            </td>
                            <td
                              className="px-3 py-2.5 whitespace-nowrap"
                              style={{ color: 'var(--theme-muted-text)' }}
                              data-testid={`form-report-source-${submission.id}`}
                            >
                              {ORIGEM[submission.source ?? 'public_link']}
                            </td>
                            <td className="px-3 py-2.5">
                              {submission.source !== undefined && submission.source !== 'public_link' ? (
                                <span
                                  className="inline-flex p-1.5"
                                  style={{ color: 'var(--theme-muted-text)' }}
                                  title="Ligada a um lead — exclua pela aba Formulários do lead"
                                  data-testid={`form-report-lead-${submission.id}`}
                                >
                                  <Icon name="lock" className="w-3.5 h-3.5" />
                                </span>
                              ) : (
                              <button
                                onClick={async () => {
                                  if (await confirm({ message: 'Excluir esta resposta?', title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' })) {
                                    deleteResponse.mutate({ formId: id!, responseId: submission.id });
                                  }
                                }}
                                data-testid={`form-report-delete-${submission.id}`}
                                className="p-1.5 rounded-lg transition-colors"
                                style={{ color: 'var(--theme-error-color)' }}
                                aria-label="Excluir resposta"
                              >
                                <Icon name="trash" className="w-3.5 h-3.5" />
                              </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Paginação (F50 — prev/next simples migra pro ListPagePagination shared) */}
                  {responses.totalPages > 1 && (
                    <ListPagePagination
                      currentPage={responsesPage}
                      totalPages={responses.totalPages}
                      onPageChange={setResponsesPage}
                      totalItems={responses.total}
                      pageSize={20}
                      itemLabel="respostas"
                    />
                  )}
                </div>
              )}
            </div>
        </div>
      </div>
    </div>
  );
};

// Card de relatório com header (título + valor auxiliar) e corpo — chrome DS
const ReportCard: React.FC<{
  title: string;
  aside?: string;
  children: React.ReactNode;
}> = ({ title, aside, children }) => (
  <div
    className="border rounded-xl overflow-hidden"
    style={{
      backgroundColor: 'var(--theme-card-background)',
      borderColor: 'var(--theme-card-border)',
    }}
  >
    <div
      className="flex items-center justify-between gap-3 px-4 py-3 border-b"
      style={{ borderColor: 'var(--theme-card-border)' }}
    >
      <h3 className="text-sm font-semibold truncate" style={{ color: 'var(--theme-card-text)' }}>
        {title}
      </h3>
      {aside && (
        <span className="text-xs tabular-nums flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }}>
          {aside}
        </span>
      )}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const MetricCard: React.FC<{
  label: string;
  value: string | number;
  testId?: string;
}> = ({ label, value, testId }) => (
  <div
    className="border rounded-xl p-4"
    style={{
      backgroundColor: 'var(--theme-card-background)',
      borderColor: 'var(--theme-card-border)',
    }}
    {...(testId ? { 'data-testid': testId } : {})}
  >
    <span className="block text-xs mb-2" style={{ color: 'var(--theme-muted-text)' }}>{label}</span>
    <p className="text-[26px] leading-none font-extrabold tracking-tight tabular-nums" style={{ color: 'var(--theme-card-text)' }}>{value}</p>
  </div>
);
